// VIP 12 - Page 1: Assessment
// 1-1. Patient Data
// 1-2. Inclusion Criteria
// 1-3. Exclusion Criteria
// Randomization (조건부)

import React, { useEffect, useState } from "react";

const Page1 = ({ formData, handleChange, handleCheckboxChange, dtDisplay }) => {
  const [showRandomization, setShowRandomization] = useState(false);
  const [randomizationResult, setRandomizationResult] = useState(null);
  const [isRandomizing, setIsRandomizing] = useState(false);

  // 디버깅: Page1 로드 시 formData 구조 출력
  useEffect(() => {
    console.log("=== [DEBUG] Page1 formData 구조 ===");
    console.log("전체 formData:", formData);
    console.log("Page1 관련 필드들:", {
      pt_number: formData.pt_number,
      pt_name: formData.pt_name,
      date_of_birth: formData.date_of_birth,
      age: formData.age,
      sex: formData.sex,
      ed_arrival_datetime: formData.ed_arrival_datetime,
      height: formData.height,
      weight: formData.weight,
      // Inclusion
      inclu_ohca: formData.inclu_ohca,
      inclu_witnessed: formData.inclu_witnessed,
      inclu_age: formData.inclu_age,
      // Exclusion
      exclu_: formData.exclu_,
      exclu_rosc: formData.exclu_rosc,
      exclu_cpc34: formData.exclu_cpc34,
      exclu_bleeding: formData.exclu_bleeding,
      exclu_aorticds: formData.exclu_aorticds,
      exclu_dead: formData.exclu_dead,
      exclu_ecmo: formData.exclu_ecmo,
      exclu_traumatic: formData.exclu_traumatic,
      exclu_request: formData.exclu_request,
      // Randomization
      timeOfRandomization: formData.timeOfRandomization,
      reboa: formData.reboa,
      confirm: formData.confirm,
      // System
      data_id: formData.data_id,
      case_id: formData.case_id,
      user_id: formData.user_id,
    });

    // 현재 전송 버튼을 누르면 어떻게 전송될지 시뮬레이션
    const filteredFormData = Object.entries(formData).reduce(
      (acc, [key, value]) => {
        if (Array.isArray(value) && value.length === 0) return acc;
        if (typeof value === "string" && value.trim() === "") return acc;
        if (value === null || value === undefined) return acc;
        return { ...acc, [key]: value };
      },
      {}
    );

    console.log("전송될 데이터 (빈 값 제외):", filteredFormData);
    console.log("전송 API 정보:", {
      endpoint: "https://product.idea2m.com/api/vip12/save",
      method: "POST",
      payload: {
        formData: filteredFormData,
        projectId: "12",
        caseId: formData.case_id || formData.data_id,
      },
    });
    console.log("=== [DEBUG] Page1 끝 ===");
  }, [formData]);
  // Randomization 조건 체크 및 시간 자동 설정을 위한 useEffect
  useEffect(() => {
    // 포함 기준 체크
    const inclusionMet =
      formData.inclu_ohca === "yes" &&
      formData.inclu_age === "yes" &&
      formData.inclu_witnessed === "witnessed";

    // 제외 기준 체크 (모두 "no"여야 함)
    const exclusionFields = [
      "exclu_traumatic",
      "exclu_",
      "exclu_rosc",
      "exclu_cpc34",
      "exclu_bleeding",
      "exclu_aorticds",
      "exclu_dead",
      "exclu_ecmo",
      "exclu_request",
    ];

    const exclusionMet = exclusionFields.every(
      (field) => formData[field] === "no"
    );

    const shouldShow = inclusionMet && exclusionMet;

    // 상태 업데이트
    if (shouldShow !== showRandomization) {
      setShowRandomization(shouldShow);

      // Randomization 필드가 처음 나타날 때 시간 자동 설정
      if (shouldShow && !formData.timeOfRandomization) {
        const now = new Date();
        const formattedTime =
          now.getFullYear() +
          "-" +
          (now.getMonth() + 1).toString().padStart(2, "0") +
          "-" +
          now.getDate().toString().padStart(2, "0") +
          " " +
          now.getHours().toString().padStart(2, "0") +
          ":" +
          now.getMinutes().toString().padStart(2, "0") +
          ":" +
          now.getSeconds().toString().padStart(2, "0");

        console.log("[Page1] Setting randomization time:", formattedTime);
        handleChange({
          target: { name: "timeOfRandomization", value: formattedTime },
        });
      }
    }
  }, [
    formData.inclu_ohca,
    formData.inclu_age,
    formData.inclu_witnessed,
    formData.exclu_traumatic,
    formData.exclu_,
    formData.exclu_rosc,
    formData.exclu_cpc34,
    formData.exclu_bleeding,
    formData.exclu_aorticds,
    formData.exclu_dead,
    formData.exclu_ecmo,
    formData.exclu_request,
    formData.timeOfRandomization,
    showRandomization,
  ]);

  // 라디오 버튼 전용 핸들러 - Randomization 조건 체크 포함
  const handleRadioClick = (e) => {
    const { name, value } = e.target;

    // 현재 선택된 값과 클릭된 값이 같으면 해제, 다르면 선택
    if (formData[name] === value) {
      handleChange({ target: { name, value: "" } });
    } else {
      handleChange({ target: { name, value } });
    }

    // useEffect에서 자동으로 조건 체크가 이루어지므로 별도 처리 불필요
  };

  // 재사용 가능한 라디오 필드 렌더링 함수
  const renderRadioField = (
    fieldName,
    label,
    options,
    isRequired = false,
    columnType = "2col"
  ) => {
    const wrapperClass =
      columnType === "1col" ? "input-wrapper-1column" : "input-wrapper-2column";
    const blockClass =
      columnType === "1col" ? "block-1column" : "block-2column";
    const titleClass =
      columnType === "1col" ? "item_title_1col" : "item_title_2col";
    const inputClass = columnType === "1col" ? "input_1col" : "input_2col";

    return (
      <div className={wrapperClass}>
        <div className={blockClass}>
          <label className={titleClass}>
            {label}
            {isRequired && <span className="required-mark"> *</span>}
          </label>
          <div className={inputClass}>
            <div className="rad-group vertical">
              {options.map((option) => (
                <label key={option.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={fieldName}
                    value={option.value}
                    checked={formData[fieldName] === option.value}
                    onClick={handleRadioClick}
                    onChange={() => {}} // React 경고 방지
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{option.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 텍스트 입력 필드 렌더링 함수
  const renderTextField = (
    fieldName,
    label,
    placeholder = "",
    unit = "",
    isRequired = false,
    type = "text",
    maxLength = null
  ) => {
    return (
      <div className="block-2column">
        <label className="item_title_2col">
          {label}
          {isRequired && <span className="required-mark"> *</span>}
        </label>
        <div className="input_2col">
          <input
            type={type}
            name={fieldName}
            placeholder={placeholder}
            value={formData[fieldName] || ""}
            onChange={handleChange}
            maxLength={maxLength}
          />
          {unit && <span className="unit-text">{unit}</span>}
        </div>
      </div>
    );
  };

  // Randomization 미리보기 함수 (DB 업데이트 없음)
  const executeRandomization = async () => {
    setIsRandomizing(true);
    try {
      console.log("[Page1] Randomization 미리보기 요청");

      // VIP12 Randomization 미리보기 API 호출
      const apiUrl =
        "https://product.idea2m.com/api/vip12/randomization/preview";
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}), // 빈 객체 전송
        mode: "cors",
        credentials: "omit",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || "Randomization 실패");
      }

      console.log("[Page1] Randomization 미리보기 결과:", result);

      // 미리보기 결과 저장 (아직 확정되지 않음)
      setRandomizationResult({
        id: result.id,
        group: result.group,
        blockId: result.id, // confirm API에서 사용할 block_id
        preview: true, // 미리보기 상태임을 표시
      });

      // formData에는 아직 저장하지 않음 (확정 후에 저장)
      console.log(`[Page1] 다음 배정 그룹: ${result.group} (미리보기)`);
    } catch (error) {
      console.error("[Page1] Randomization 미리보기 오류:", error);
      alert(`Randomization error occurred: ${error.message}`);
    } finally {
      setIsRandomizing(false);
    }
  };

  // Randomization 확정 함수 (실제 DB 업데이트)
  const confirmRandomization = async () => {
    if (!randomizationResult || !randomizationResult.blockId) {
      alert("No randomization result to confirm.");
      return;
    }

    try {
      console.log(
        "[Page1] Randomization 확정 요청:",
        randomizationResult.blockId
      );

      // VIP12 Randomization 확정 API 호출
      const apiUrl =
        "https://product.idea2m.com/api/vip12/randomization/confirm";
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          blockId: randomizationResult.blockId,
        }),
        mode: "cors",
        credentials: "omit",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || "Randomization 확정 실패");
      }

      console.log("[Page1] Randomization 확정 완료:", result);

      // 확정된 결과를 formData에 저장
      handleChange({ target: { name: "reboa", value: result.group } });
      handleChange({ target: { name: "confirm", value: "yes" } });

      // randomizationResult 업데이트 (확정 상태로 변경)
      setRandomizationResult({
        ...randomizationResult,
        confirmed: true,
        preview: false,
        statistics: result.statistics,
      });

      alert(`Randomization confirmed: Assigned to ${result.group} group.`);
    } catch (error) {
      console.error("[Page1] Randomization 확정 오류:", error);
      alert(`Randomization confirmation error occurred: ${error.message}`);
    }
  };

  return (
    <section className="vip-form-section">
      <h2 className="section-subtitle">Assessment</h2>

      {/* 1-1. Patient Data */}
      <div className="basic-info-fieldset">
        <h3 className="section-subtitle">1-1. Patient Data</h3>

        <div className="input-wrapper-2column">
          {renderTextField("pt_number", "Study ID")}
          {renderTextField(
            "pt_name",
            "Patient name(Initial)",
            "",
            "",
            false,
            "text",
            3
          )}
        </div>

        <div className="input-wrapper-2column">
          {renderTextField(
            "date_of_birth",
            "Date of birth",
            "",
            "",
            false,
            "date"
          )}
          {renderTextField("age", "Age")}
        </div>

        {renderRadioField(
          "sex",
          "Sex",
          [
            { label: "male", value: "male" },
            { label: "female", value: "female" },
          ],
          true
        )}

        {renderTextField(
          "ed_arrival_datetime",
          "ED arrival date & time",
          "",
          "",
          true,
          "datetime-local"
        )}

        <div className="input-wrapper-2column">
          {renderTextField("height", "Height", "", "cm")}
          {renderTextField("weight", "Weight", "", "kg")}
        </div>

        <div className="input-wrapper-2column">
          {renderTextField(
            "waist_circumference",
            "Waist circumference",
            "",
            "cm"
          )}
          {renderTextField("hip_circumference", "Hip circumference", "", "cm")}
        </div>
      </div>

      {/* 1-2. Inclusion Criteria */}
      <div className="inclusion-criteria-fieldset">
        <h3 className="section-subtitle">1-2. Inclusion Criteria</h3>

        {renderRadioField(
          "inclu_ohca",
          "Out of hospital cardiac arrest",
          [
            { label: "yes", value: "yes" },
            { label: "no", value: "no" },
          ],
          true
        )}

        {renderRadioField(
          "inclu_witnessed",
          "Witnessed arrest",
          [
            { label: "witnessed", value: "witnessed" },
            { label: "unwitnessed", value: "unwitnessed" },
          ],
          true
        )}

        {renderRadioField(
          "inclu_age",
          "Age: 20~80",
          [
            { label: "yes", value: "yes" },
            { label: "no", value: "no" },
          ],
          true
        )}
      </div>

      {/* 1-3. Exclusion Criteria */}
      <div className="exclusion-criteria-fieldset">
        <h3 className="section-subtitle">1-3. Exclusion Criteria</h3>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            {renderRadioField(
              "exclu_",
              "Pregnancy",
              [
                { label: "yes", value: "yes" },
                { label: "no", value: "no" },
              ],
              true,
              "2col"
            )}
          </div>
          <div className="block-2column">
            {renderRadioField(
              "exclu_rosc",
              "ROSC before randomization",
              [
                { label: "yes", value: "yes" },
                { label: "no", value: "no" },
              ],
              true,
              "2col"
            )}
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            {renderRadioField(
              "exclu_cpc34",
              "Baseline CPC 3-4",
              [
                { label: "yes", value: "yes" },
                { label: "no", value: "no" },
              ],
              true,
              "2col"
            )}
          </div>
          <div className="block-2column">
            {renderRadioField(
              "exclu_bleeding",
              "Evidence of cardiac arrest due to bleeding",
              [
                { label: "yes", value: "yes" },
                { label: "no", value: "no" },
              ],
              true,
              "2col"
            )}
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            {renderRadioField(
              "exclu_aorticds",
              "Suspected aortic disease in bedside USG or history of aortic disease",
              [
                { label: "yes", value: "yes" },
                { label: "no", value: "no" },
              ],
              true,
              "2col"
            )}
          </div>
          <div className="block-2column">
            {renderRadioField(
              "exclu_dead",
              "Declared dead before enrollment",
              [
                { label: "yes", value: "yes" },
                { label: "no", value: "no" },
              ],
              true,
              "2col"
            )}
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            {renderRadioField(
              "exclu_ecmo",
              "Decided to apply ECMO resuscitation before enrollment",
              [
                { label: "yes", value: "yes" },
                { label: "no", value: "no" },
              ],
              true,
              "2col"
            )}
          </div>
          <div className="block-2column">
            {renderRadioField(
              "exclu_traumatic",
              "Traumatic cardiac arrest",
              [
                { label: "yes", value: "yes" },
                { label: "no", value: "no" },
              ],
              true,
              "2col"
            )}
          </div>
        </div>

        {renderRadioField(
          "exclu_request",
          "Legal representatives request termination of resuscitation efforts before enrollment",
          [
            { label: "yes", value: "yes" },
            { label: "no", value: "no" },
          ],
          true
        )}
      </div>

      {/* Randomization 섹션 (조건부 표시) */}
      {showRandomization && (
        <div
          className="randomization-fieldset"
          style={{
            backgroundColor: "#fff3cd",
            border: "2px solid #ffc107",
            borderRadius: "8px",
            padding: "20px",
            margin: "20px 0",
          }}
        >
          <h3 className="section-subtitle" style={{ color: "#856404" }}>
            Randomization
          </h3>

          <div className="input-wrapper-2column">
            <div className="block-2column">
              <label className="item_title_2col">Time of Randomization</label>
              <div className="input_2col">
                <input
                  type="text"
                  name="timeOfRandomization"
                  value={formData.timeOfRandomization || ""}
                  readOnly
                  style={{ backgroundColor: "#f8f9fa", color: "#495057" }}
                />
              </div>
            </div>
            <div className="block-2column">
              <div className="input_2col"></div>
            </div>
          </div>

          <div style={{ textAlign: "center", margin: "20px 0" }}>
            <h4 style={{ color: "#856404", marginBottom: "20px" }}>
              Execute REBOA Block randomization
            </h4>

            {!randomizationResult ? (
              <button
                type="button"
                onClick={executeRandomization}
                disabled={isRandomizing}
                style={{
                  padding: "12px 30px",
                  backgroundColor: "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  fontSize: "16px",
                  fontWeight: "bold",
                  cursor: isRandomizing ? "not-allowed" : "pointer",
                  opacity: isRandomizing ? 0.6 : 1,
                }}
              >
                {isRandomizing ? "Randomizing..." : "Randomization"}
              </button>
            ) : (
              <div>
                <div
                  style={{
                    fontSize: "20px",
                    marginBottom: "20px",
                    padding: "15px",
                    backgroundColor: randomizationResult.confirmed
                      ? "#d4edda"
                      : "#fff3cd",
                    border: randomizationResult.confirmed
                      ? "1px solid #c3e6cb"
                      : "1px solid #ffc107",
                    borderRadius: "5px",
                    color: randomizationResult.confirmed
                      ? "#155724"
                      : "#856404",
                  }}
                >
                  <strong>Block ID:</strong> {randomizationResult.id}
                  <br />
                  <strong>Group:</strong> {randomizationResult.group}
                  <br />
                  <br />
                  {randomizationResult.confirmed ? (
                    <span style={{ fontWeight: "bold", color: "#155724" }}>
                      ✅ Randomization has been confirmed.
                    </span>
                  ) : (
                    <span style={{ fontWeight: "bold", color: "#856404" }}>
                      ⚠️ Preview mode. Click Confirm button to finalize.
                    </span>
                  )}
                </div>

                {!randomizationResult.confirmed && (
                  <button
                    type="button"
                    onClick={confirmRandomization}
                    style={{
                      padding: "12px 30px",
                      backgroundColor: "#28a745",
                      color: "white",
                      border: "none",
                      borderRadius: "5px",
                      fontSize: "16px",
                      fontWeight: "bold",
                      cursor: "pointer",
                    }}
                  >
                    Confirm
                  </button>
                )}

                {randomizationResult.confirmed &&
                  randomizationResult.statistics && (
                    <div
                      style={{
                        marginTop: "15px",
                        fontSize: "14px",
                        color: "#666",
                      }}
                    >
                      <strong>Statistics:</strong> REBOA{" "}
                      {randomizationResult.statistics.reboa_count}, Control{" "}
                      {randomizationResult.statistics.non_reboa_count} (Total{" "}
                      {randomizationResult.statistics.used_items} assigned)
                    </div>
                  )}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default Page1;
